"use client";

import Link from "next/link";
import { useSession } from "next-auth/react";
import { useMemo } from "react";
import { Logo } from "@/components/Logo";
import { COMPANY_PLAN_FROM_MONTHLY_GBP, formatCompanyPlanPrice } from "@/lib/company-plans";
import { getCustomerSiteUrl } from "@/lib/sites/domains";

const CUSTOMER_NAV_LINKS = [
  { label: "Find scaffolder", href: "/scaffolders" },
  { label: "Find work", href: "/post-job" },
  { label: "News", href: "/news" },
  { label: "About us", href: "/#about" },
];

type PublicHeaderProps = {
  /** Use on homepage where full nav (anchors) applies. Set false on other pages to hide nav. */
  showNav?: boolean;
  /** Max width class for inner content (e.g. max-w-6xl or max-w-4xl) */
  maxWidth?: string;
  /** customer = Scaffolder Near Me (post job, browse); scaffolder = ScaffOps™ (app, join) */
  site?: "customer" | "scaffolder";
};

export function PublicHeader({ showNav = true, maxWidth = "max-w-6xl", site }: PublicHeaderProps) {
  const { data: session, status } = useSession();

  const resolvedSite = useMemo(() => {
    if (site) return site;
    if (typeof window === "undefined") return "customer";
    const h = window.location.hostname.toLowerCase();
    const scaffApex =
      process.env.NEXT_PUBLIC_SCAFFOLDER_DOMAIN?.split(":")[0]?.toLowerCase() || "scaffer.co.uk";
    const matches =
      h === scaffApex || h === `www.${scaffApex}` || h.endsWith(`.${scaffApex}`);
    return matches ? "scaffolder" : "customer";
  }, [site]);

  const isScaffolder = resolvedSite === "scaffolder";

  return (
    <header className="sticky top-0 z-50 border-b border-brand-dark/20 bg-brand-dark px-4 py-3">
      <div className={`mx-auto flex ${maxWidth} items-center justify-between gap-4`}>
        <Link href="/" className="flex min-w-0 items-center gap-2">
          <Logo size={36} className="shrink-0 text-white" />
          <span className="hidden font-semibold text-white sm:inline">
            {isScaffolder ? "ScaffOps™" : "Scaffolder Near Me"}
          </span>
        </Link>
        {showNav && !isScaffolder && (
          <nav className="hidden items-center gap-6 md:flex">
            {CUSTOMER_NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="text-sm text-white/90 hover:text-white"
              >
                {link.label}
              </Link>
            ))}
          </nav>
        )}
        {showNav && isScaffolder && (
          <nav className="hidden items-center gap-6 md:flex">
            <a
              href={getCustomerSiteUrl("/post-job")}
              className="text-sm text-white/90 hover:text-white"
            >
              Post a job
            </a>
            <Link href="/pricing" className="text-sm text-white/90 hover:text-white">
              Pricing
            </Link>
          </nav>
        )}
        <div className="flex shrink-0 items-center gap-2 md:gap-3">
          {status === "loading" ? (
            <span className="text-sm text-white/70">…</span>
          ) : session?.user ? (
            <>
              <Link
                href={(session.user as { companyId?: string }).companyId ? "/app" : "/dashboard"}
                className="rounded-lg border border-white/30 px-3 py-2 text-sm text-white hover:bg-white/10"
              >
                Dashboard
              </Link>
              <Link
                href="/signout"
                className="hidden rounded-lg border border-white/30 px-3 py-2 text-sm text-white hover:bg-white/10 md:inline-block"
              >
                Sign out
              </Link>
            </>
          ) : (
            <>
              <Link
                href={isScaffolder ? "/signup" : "/pricing"}
                className="hidden flex-col items-end text-sm font-medium text-white/90 hover:text-white md:flex md:flex-row md:items-center"
              >
                <span>{isScaffolder ? "Join" : "Add business"}</span>
                {!isScaffolder && (
                  <span className="text-xs text-white/70 md:ml-1">
                    from {formatCompanyPlanPrice(COMPANY_PLAN_FROM_MONTHLY_GBP)} + VAT/mo
                  </span>
                )}
              </Link>
              {isScaffolder ? (
                <a
                  href={getCustomerSiteUrl("/post-job")}
                  className="hidden rounded-lg border border-white/30 px-4 py-2 text-sm font-medium text-white hover:bg-white/10 md:inline-block"
                >
                  Post a job
                </a>
              ) : null}
              <Link
                href={isScaffolder ? getCustomerSiteUrl("/post-job") : "/post-job"}
                className="whitespace-nowrap rounded-lg bg-brand-yellow px-3 py-2 text-sm font-semibold text-brand-dark hover:bg-brand-yellow/90 sm:px-4"
              >
                Get a Quote
              </Link>
              <Link
                href="/login"
                className="hidden items-center gap-2 rounded-lg border border-white/30 px-3 py-2 text-sm text-white hover:bg-white/10 md:flex"
              >
                <span className="size-5 rounded-full border border-white/50" aria-hidden />
                Login
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
